import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid, Stepper, Step, StepButton } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBackIos';
import ArrowForward from '@mui/icons-material/ArrowForwardIos';
import { Button, Card, Typography } from '@totalsoft/rocket-ui';
import { OfferStepLabel, OfferStepperWrapper } from './OfferStyles';

const getPreviousStep = (stepIndex, steps) => {
  stepIndex--;
  if (stepIndex < 0) {
    return null;
  }
  const step = steps[stepIndex];
  return step;
};

const getNextStep = (stepIndex, steps) => {
  if (stepIndex === -1) {
    return null;
  }
  stepIndex++;

  if (stepIndex >= steps.length) {
    return null;
  }

  const step = steps[stepIndex];
  return step;
};

const getPreviousDisabled = (activeStepIndex, steps) => {
  const step = getPreviousStep(activeStepIndex, steps);
  return !step || step.disabled;
};

const getNextDisabled = (activeStepIndex, steps) => {
  const step = getNextStep(activeStepIndex, steps);
  return !step || step.disabled;
};

const getEnabledStepsIndex = steps => {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (!steps[i].disabled) return i;
  }
};

const OfferStepper = props => {
  const { t, steps, onStepClick, isImage, processing, activeStepId } = props;
  const [stepperOrientation, setStepperOrientation] = useState('horizontal');

  const activeStepIndex = useMemo(() => steps.findIndex(step => step.id === activeStepId), [steps, activeStepId]);

  const lastActiveStepIndex = useMemo(() => getEnabledStepsIndex(steps, activeStepIndex), [activeStepIndex, steps]);

  const previousDisabled = useMemo(() => getPreviousDisabled(activeStepIndex, steps), [activeStepIndex, steps]);
  const nextDisabled = useMemo(() => getNextDisabled(activeStepIndex, steps), [activeStepIndex, steps]);

  const forceUpdate = useCallback(() => {
    if (window.innerWidth < 600) setStepperOrientation('vertical');
    else setStepperOrientation('horizontal');
  }, []);

  const resize = useCallback(() => forceUpdate(), [forceUpdate]);

  const onPreviousClick = useCallback(() => {
    const step = getPreviousStep(activeStepIndex, steps);
    onStepClick(step);
  }, [steps, onStepClick, activeStepIndex]);

  const onNextClick = useCallback(() => {
    const step = getNextStep(activeStepIndex, steps);
    onStepClick(step);
  }, [steps, onStepClick, activeStepIndex]);

  useEffect(() => {
    if (window.innerWidth < 600) {
      setStepperOrientation('vertical');
    }
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [resize]);

  const createStep = (step, idx) => {
    let tSteps = [];
    tSteps.push(
      <Step disabled={step.disabled || processing} key={step.id}>
        <StepButton
          //active={lens.ceva |> get}
          id={'offerStepper_' + t(step.title).toLowerCase()}
          onClick={() => onStepClick(step)}
          disabled={step.disabled || processing}
          optional={
            <Typography variant='caption' color='error'>
              {step.error}
            </Typography>
          }
        >
          {idx === activeStepIndex ? (
            <OfferStepLabel error={step.error}>
              <b>{t(step.title)}</b>
            </OfferStepLabel>
          ) : (
            <OfferStepLabel error={step.error}>{t(step.title)}</OfferStepLabel>
          )}
        </StepButton>
      </Step>
    );

    if (step.nextSteps.length > 0) {
      tSteps.push(
        <Step key={'nextStep'}>
          {step.nextSteps.map(nextStep => (
            <StepButton
              color='red'
              id={'offerStepper_' + t(nextStep.title).toLowerCase()}
              onClick={() => onStepClick(nextStep)}
              disabled={nextStep.disabled || processing}
              key={nextStep.id}
              optional={
                <Typography variant='caption' color='error'>
                  {nextStep.error}
                </Typography>
              }
            >
              <OfferStepLabel error={nextStep.error} StepIconProps={{ active: !nextStep.disabled && !processing }}>
                {t(nextStep.title)}
              </OfferStepLabel>
            </StepButton>
          ))}
        </Step>
      );
    }
    return tSteps;
  };

  return (
    <Card disablePadding sx={{ p: 2 }}>
      <Grid container alignItems='center'>
        {!isImage && (
          <Grid item>
            <Button id={'offerStepper_previousBtn'} variant='text' disabled={previousDisabled || processing} onClick={onPreviousClick}>
              <ArrowBack />
              {t('Previous')}
            </Button>
          </Grid>
        )}
        <OfferStepperWrapper item>
          <Stepper activeStep={lastActiveStepIndex} orientation={stepperOrientation}>
            {steps.map((step, idx) => createStep(step, idx))}
          </Stepper>
        </OfferStepperWrapper>
        {!isImage && (
          <Grid item>
            <Button id={'offerStepper_nextBtn'} variant='text' disabled={nextDisabled || processing} onClick={onNextClick}>
              {t('Next')}
              <ArrowForward />
            </Button>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

OfferStepper.propTypes = {
  t: PropTypes.func.isRequired,
  steps: PropTypes.array.isRequired,
  onStepClick: PropTypes.func.isRequired,
  activeStepId: PropTypes.number.isRequired,
  isImage: PropTypes.bool,
  processing: PropTypes.bool
};

export default compose(withTranslation())(OfferStepper);
